import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { UserProfile, CompanyAssignment } from '@/types/api'

interface ClerkUser {
  id: string
  email: string
  firstName: string
  lastName: string
  imageUrl?: string
}

interface UserState {
  // Clerk user data (from Clerk SDK)
  clerkUser: ClerkUser | null

  // Backend user profile (from /auth/me)
  profile: UserProfile | null

  // Loading states
  isClerkLoaded: boolean
  isProfileLoading: boolean
  profileError: string | null

  // Actions
  setClerkUser: (user: ClerkUser | null) => void
  setProfile: (profile: UserProfile | null) => void
  setProfileLoading: (loading: boolean) => void
  setProfileError: (error: string | null) => void
  clearUser: () => void
}

export const useUserStore = create<UserState>()(
  devtools(
    (set) => ({
      clerkUser: null,
      profile: null,
      isClerkLoaded: false,
      isProfileLoading: false,
      profileError: null,

      setClerkUser: (clerkUser) =>
        set({
          clerkUser,
          isClerkLoaded: true,
        }),

      setProfile: (profile) =>
        set({
          profile,
          isProfileLoading: false,
          profileError: null,
        }),

      setProfileLoading: (isProfileLoading) => set({ isProfileLoading }),

      setProfileError: (profileError) =>
        set({
          profileError,
          isProfileLoading: false,
        }),

      clearUser: () =>
        set({
          clerkUser: null,
          profile: null,
          isClerkLoaded: false,
          isProfileLoading: false,
          profileError: null,
        }),
    }),
    {
      name: 'user-store',
    }
  )
)

// Selectors for common use cases
export const selectUser = (state: UserState) => state.profile
export const selectClerkUser = (state: UserState) => state.clerkUser
export const selectIsAuthenticated = (state: UserState) =>
  !!state.clerkUser && !!state.profile
export const selectIsInternal = (state: UserState) =>
  state.profile?.userType === 'INTERNAL'
export const selectRole = (state: UserState) => state.profile?.role ?? null
export const selectAllowedApps = (state: UserState): string[] =>
  state.profile?.allowedApps ?? []
export const selectHasCompassAccess = (state: UserState) =>
  (state.profile?.allowedApps ?? []).includes('compass')
export const selectCapabilities = (state: UserState): string[] =>
  state.profile?.capabilities ?? []
export const selectCompanyAssignments = (
  state: UserState
): CompanyAssignment[] => state.profile?.companyAssignments ?? []
export const selectIsProfileReady = (state: UserState) =>
  state.isClerkLoaded && !state.isProfileLoading && !!state.profile
